import { createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from '../../utils/api';
import {
  ICreateQualificationPayload,
  ICreateQualificationResponse,
  ICreateRolePayload,
  ICreateRoleResponse,
  ICreateFacilityTypePayload,
  ICreateFacilityTypeResponse,
  ICreateDocumentTypePayload,
  ICreateDocumentTypeResponse,
  ICreateDesignationPayload,
  ICreateDesignationResponse,
  ICreateRoleDocumentMappingPayload,
  ICreateRoleDocumentMappingResponse,
  IGetRoleDocumentMappingResponse,
  ICreateFacilityTypeDocumentMappingPayload,
  ICreateFacilityTypeDocumentMappingResponse,
  IGetFacilityTypeDocumentMappingResponse,
  ICreateCancellationReasonPayload,
  IUpdateCancellationReasonPayload,
  ISearchCancellationReasonsResponse,
  ICreateConfigPayload,
  ICreateConfigResponse,
  IUpdateConfigPayload,
  IUpdateConfigResponse,
  IListConfigResponse,
  CustomForm,
  CustomFormCreatePayload,
  CustomFormUpdatePayload,
  IMapInputsToFormPayload,
  IMapInputsToFormResponse,
  IPublishCustomFormPayload,
  IPublishCustomFormResponse,
  IGetCustomFormFieldsResponse,
  IAddCustomFormFieldPayload,
  IAddCustomFormFieldResponse,
  IDeleteCustomFormFieldResponse,
  IUpdateCustomFormFieldPayload,
  IUpdateCustomFormFieldResponse,
  IGetBankFormFieldsResponse,
  IBankFormListResponse,
  ICreateBankFormFieldPayload,
  ICreateBankFormFieldResponse,
  IUpdateBankFormFieldPayload,
  IUpdateBankFormFieldResponse,
  IDeleteBankFormFieldResponse,
  ISoftDeleteBankFormFieldResponse
} from './types';

const getErrorMessage = (error: any, defaultMessage: string) => {
  if (error.response?.data?.data) {
    const errorData = error.response.data.data;
    let errors: string[] = [];
    if (typeof errorData === 'object' && errorData !== null) {
      for (const key in errorData) {
        if (Object.prototype.hasOwnProperty.call(errorData, key)) {
          const messages = errorData[key];
          if (Array.isArray(messages)) {
            errors.push(`${key}: ${messages.join(', ')}`);
          } else {
            errors.push(`${key}: ${messages}`);
          }
        }
      }
    }

    if (errors.length > 0) {
      return errors.join('\n');
    }
  }
  return error.response?.data?.message || defaultMessage;
};




export const createQualification = createAsyncThunk(
  'masterdata/createQualification',
  async (payload: ICreateQualificationPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateQualificationResponse>('post', '/auth/qualification/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create qualification'));
    }
  }
);

export const searchQualifications = createAsyncThunk(
  'masterdata/searchQualifications',
  async (params: { search: string; page: number; page_size: number }, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams({
        search: params.search,
        page: params.page.toString(),
        page_size: params.page_size.toString()
      }).toString();
      const response = await apiCall<any>('get', `/auth/qualification/list/?${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search qualifications'));
    }
  }
);

export const updateQualification = createAsyncThunk(
  'masterdata/updateQualification',
  async (payload: any, { rejectWithValue }) => { // Using explicit type locally to avoid circular dependency if complex, but here simplistic
    try {
      const { id, ...data } = payload;
      const response = await apiCall<any>('put', `/ auth / qualification / ${id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update qualification'));
    }
  }
);

export const deleteQualification = createAsyncThunk(
  'masterdata/deleteQualification',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/auth/qualification/${id}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete qualification'));
    }
  }
);

// Role Actions

export const createRole = createAsyncThunk(
  'masterdata/createRole',
  async (payload: ICreateRolePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateRoleResponse>('post', '/auth/role/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create role'));
    }
  }
);

export const searchRoles = createAsyncThunk(
  'masterdata/searchRoles',
  async (params: { search: string; page: number; page_size: number }, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams({
        search: params.search,
        page: params.page.toString(),
        page_size: params.page_size.toString()
      }).toString();
      const response = await apiCall<any>('get', `/auth/role/list/?${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search roles'));
    }
  }
);

export const updateRole = createAsyncThunk(
  'masterdata/updateRole',
  async (payload: any, { rejectWithValue }) => {
    try {
      const { id, ...data } = payload;
      const response = await apiCall<any>('put', `/auth/role/${id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update role'));
    }
  }
);

export const deleteRole = createAsyncThunk(
  'masterdata/deleteRole',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/auth/role/${id}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete role'));
    }
  }
);

// Facility Type Actions

export const createFacilityType = createAsyncThunk(
  'masterdata/createFacilityType',
  async (payload: ICreateFacilityTypePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateFacilityTypeResponse>('post', '/auth/facility-type/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create facility type'));
    }
  }
);

export const searchFacilityTypes = createAsyncThunk(
  'masterdata/searchFacilityTypes',
  async (params: { search: string; page: number; page_size: number }, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams({
        search: params.search,
        page: params.page.toString(),
        page_size: params.page_size.toString()
      }).toString();
      const response = await apiCall<any>('get', `/auth/facility-type/list/?${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search facility types'));
    }
  }
);

export const updateFacilityType = createAsyncThunk(
  'masterdata/updateFacilityType',
  async (payload: any, { rejectWithValue }) => {
    try {
      const { id, ...data } = payload;
      const response = await apiCall<any>('put', `/auth/facility-type/${id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update facility type'));
    }
  }
);

export const deleteFacilityType = createAsyncThunk(
  'masterdata/deleteFacilityType',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/auth/facility-type/${id}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete facility type'));
    }
  }
);

// Document Type Actions

export const createDocumentType = createAsyncThunk(
  'masterdata/createDocumentType',
  async (payload: ICreateDocumentTypePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateDocumentTypeResponse>('post', '/auth/document-type/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create document type'));
    }
  }
);

export const searchDocumentTypes = createAsyncThunk(
  'masterdata/searchDocumentTypes',
  async (params: { search: string; page: number; page_size: number }, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams({
        search: params.search,
        page: params.page.toString(),
        page_size: params.page_size.toString()
      }).toString();
      const response = await apiCall<any>('get', `/auth/document-type/paginated-list/?${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search document types'));
    }
  }
);

export const updateDocumentType = createAsyncThunk(
  'masterdata/updateDocumentType',
  async (payload: any, { rejectWithValue }) => {
    try {
      const { id, ...data } = payload;
      const response = await apiCall<any>('put', `/auth/document-type/${id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update document type'));
    }
  }
);

export const deleteDocumentType = createAsyncThunk(
  'masterdata/deleteDocumentType',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/auth/document-type/${id}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete document type'));
    }
  }
);

// Designation Actions

export const createDesignation = createAsyncThunk(
  'masterdata/createDesignation',
  async (payload: ICreateDesignationPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateDesignationResponse>('post', '/auth/designation/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create designation'));
    }
  }
);

export const searchDesignations = createAsyncThunk(
  'masterdata/searchDesignations',
  async (params: { search: string; page: number; page_size: number }, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams({
        search: params.search,
        page: params.page.toString(),
        page_size: params.page_size.toString()
      }).toString();
      const response = await apiCall<any>('get', `/auth/designation/list/?${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search designations'));
    }
  }
);

export const updateDesignation = createAsyncThunk(
  'masterdata/updateDesignation',
  async (payload: any, { rejectWithValue }) => {
    try {
      const { id, ...data } = payload;
      const response = await apiCall<any>('put', `/auth/designation/${id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update designation'));
    }
  }
);

export const deleteDesignation = createAsyncThunk(
  'masterdata/deleteDesignation',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/auth/designation/${id}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete designation'));
    }
  }
);

// Role Document Mapping Actions

export const createRoleDocumentMapping = createAsyncThunk(
  'masterdata/createRoleDocumentMapping',
  async (payload: ICreateRoleDocumentMappingPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateRoleDocumentMappingResponse>('post', '/api/organization/role-document-mapping/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create role document mapping'));
    }
  }
);

export const getRoleDocumentMapping = createAsyncThunk(
  'masterdata/getRoleDocumentMapping',
  async (roleId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<IGetRoleDocumentMappingResponse>('get', `/api/organization/role-document-mapping/?role_id=${roleId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get role document mapping'));
    }
  }
);

// Facility Type Document Mapping Actions

export const createFacilityTypeDocumentMapping = createAsyncThunk(
  'masterdata/createFacilityTypeDocumentMapping',
  async (payload: ICreateFacilityTypeDocumentMappingPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateFacilityTypeDocumentMappingResponse>('post', '/api/organization/facility-type-document-mapping/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create facility type document mapping'));
    }
  }
);

export const getFacilityTypeDocumentMapping = createAsyncThunk(
  'masterdata/getFacilityTypeDocumentMapping',
  async (facilityTypeId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<IGetFacilityTypeDocumentMappingResponse>('get', `/api/organization/facility-type-document-mapping/?facility_type_id=${facilityTypeId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get facility type document mapping'));
    }
  }
);

// Cancellation Reasons Actions

export const searchCancellationReasons = createAsyncThunk(
  'masterdata/searchCancellationReasons',
  async (params: { search: string; page: number; page_size: number }, { rejectWithValue }) => {
    try {
      const queryString = params.search ? `?search=${params.search}` : '';
      const response = await apiCall<ISearchCancellationReasonsResponse>('get', `/auth/cancellation-reasons/${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search cancellation reasons'));
    }
  }
);

export const createCancellationReason = createAsyncThunk(
  'masterdata/createCancellationReason',
  async (payload: ICreateCancellationReasonPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/auth/cancellation-reasons/', payload);
      if (response.data && response.data.status && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create cancellation reason'));
    }
  }
);

export const updateCancellationReason = createAsyncThunk(
  'masterdata/updateCancellationReason',
  async (payload: IUpdateCancellationReasonPayload, { rejectWithValue }) => {
    try {
      const { id, ...data } = payload;
      const response = await apiCall<any>('patch', `/auth/cancellation-reasons/${id}/`, data);
      if (response.data && response.data.status && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update cancellation reason'));
    }
  }
);

export const deleteCancellationReason = createAsyncThunk(
  'masterdata/deleteCancellationReason',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ status: boolean; message: string }>('delete', `/auth/cancellation-reasons/${id}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete cancellation reason'));
    }
  }
);

// Bank Actions

export const searchBanks = createAsyncThunk(
  'masterdata/searchBanks',
  async (params: { search: string; page?: number; page_size?: number }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());

      const response = await apiCall<any>('get', `/auth/banks/?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search banks'));
    }
  }
);

export const uploadBankCSV = createAsyncThunk(
  'masterdata/uploadBankCSV',
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiCall<{ status: boolean; message: string }>('post', '/auth/banks/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to upload bank CSV'));
    }
  }
);

// Config Actions

export const createConfig = createAsyncThunk(
  'masterdata/createConfig',
  async (payload: ICreateConfigPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateConfigResponse>('post', '/auth/config/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create config'));
    }
  }
);

export const searchConfigs = createAsyncThunk(
  'masterdata/searchConfigs',
  async (params: { search: string; page?: number; page_size?: number }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());

      const response = await apiCall<any>('get', `/auth/config/?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to search configs'));
    }
  }
);

export const updateConfig = createAsyncThunk(
  'masterdata/updateConfig',
  async (payload: IUpdateConfigPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<IUpdateConfigResponse>('put', '/auth/config/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update config'));
    }
  }
);

export const deleteConfig = createAsyncThunk(
  'masterdata/deleteConfig',
  async (key: string, { rejectWithValue }) => {
    try {
      // Assuming delete might need key or id. Since update uses key in body, delete might use key in URL or body.
      // Standard REST often uses ID. But if key is unique...
      // Let's assume DELETE /auth/config/?key=... or /auth/config/:key/
      // Given the unusual PUT, I'll try DELETE /auth/config/ with data or query param?
      // Or maybe DELETE /auth/config/:key/
      // I'll guess DELETE /auth/config/:key/ for now, or wait for clarification.
      // Actually, I'll assume the list has IDs and we delete by ID if possible.
      // But if user didn't provide DELETE info, I might skip it or just add a placeholder.
      // I'll add a delete action assuming key path parameter.
      const response = await apiCall<any>('delete', `/auth/config/${key}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete config'));
    }
  }
);

export const listConfigs = createAsyncThunk(
  'masterdata/listConfigs',
  async (orgId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<IListConfigResponse>('get', `/auth/config/list/?org=${orgId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to list configs'));
    }
  }
);

// Custom Form Actions

export const fetchCustomForms = createAsyncThunk(
  'masterdata/fetchCustomForms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: CustomForm[] }>('get', '/api/organization/form/');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch custom forms'));
    }
  }
);

export const createCustomForm = createAsyncThunk(
  'masterdata/createCustomForm',
  async (payload: CustomFormCreatePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: CustomForm; message: string }>('post', '/api/organization/form/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create custom form'));
    }
  }
);

export const updateCustomForm = createAsyncThunk(
  'masterdata/updateCustomForm',
  async (payload: CustomFormUpdatePayload, { rejectWithValue }) => {
    try {
      const { form_id, ...data } = payload;
      const response = await apiCall<{ data: CustomForm; message: string }>('put', `/api/organization/form/update/${form_id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update custom form'));
    }
  }
);

export const mapInputsToForm = createAsyncThunk(
  'masterdata/mapInputsToForm',
  async (payload: IMapInputsToFormPayload, { rejectWithValue }) => {
    try {
      const { formId, inputs } = payload;
      const response = await apiCall<IMapInputsToFormResponse>('post', `/api/organization/form/map-inputs/${formId}/`, inputs);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to map inputs to form'));
    }
  }
);

export const deleteCustomForm = createAsyncThunk(
  'masterdata/deleteCustomForm',
  async (formId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ status: boolean; message: string }>('put', `/api/organization/form/delete/${formId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete custom form'));
    }
  }
);

export const publishCustomForm = createAsyncThunk(
  'masterdata/publishCustomForm',
  async (payload: IPublishCustomFormPayload, { rejectWithValue }) => {
    try {
      const { form_id, is_published } = payload;
      const response = await apiCall<IPublishCustomFormResponse>('put', `/api/organization/form/publish/${form_id}/`, { is_published });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to publish/unpublish custom form'));
    }
  }
);



export const getCustomFormFields = createAsyncThunk(
  'masterdata/getCustomFormFields',
  async (formId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<IGetCustomFormFieldsResponse>('get', `/api/organization/form/map-inputs/${formId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get custom form fields'));
    }
  }
);

export const addCustomFormField = createAsyncThunk(
  'masterdata/addCustomFormField',
  async (payload: IAddCustomFormFieldPayload, { rejectWithValue }) => {
    try {
      const { form_id, ...data } = payload;
      const response = await apiCall<IAddCustomFormFieldResponse>('post', `/api/organization/form/map-bank-field/${form_id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to add custom form field'));
    }
  }
);

export const deleteCustomFormField = createAsyncThunk(
  'masterdata/deleteCustomFormField',
  async (fieldId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<IDeleteCustomFormFieldResponse>('delete', `/api/organization/form/field/delete/${fieldId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete custom form field'));
    }
  }
);


export const updateCustomFormField = createAsyncThunk(
  'masterdata/updateCustomFormField',
  async (payload: IUpdateCustomFormFieldPayload, { rejectWithValue }) => {
    try {
      const { field_id, ...data } = payload;
      const response = await apiCall<IUpdateCustomFormFieldResponse>('put', `/api/organization/form/field/update/${field_id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update custom form field'));
    }
  }
);

// Bank Form Fields Actions

export const getBankFormFields = createAsyncThunk(
  'masterdata/getBankFormFields',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<IGetBankFormFieldsResponse>('get', '/api/organization/bank-form-field/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get bank form fields'));
    }
  }
);

export const getBankForms = createAsyncThunk(
  'masterdata/getBankForms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<IBankFormListResponse>('get', '/api/organization/bank-form/list/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get bank forms'));
    }
  }
);

export const createBankFormField = createAsyncThunk(
  'masterdata/createBankFormField',
  async (payload: ICreateBankFormFieldPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICreateBankFormFieldResponse>('post', '/api/organization/bank-form/create/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create bank form field'));
    }
  }
);

export const updateBankFormField = createAsyncThunk(
  'masterdata/updateBankFormField',
  async (payload: IUpdateBankFormFieldPayload, { rejectWithValue }) => {
    try {
      const { field_id, ...data } = payload;
      const response = await apiCall<IUpdateBankFormFieldResponse>('put', `/api/organization/bank-form/update/${field_id}/`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update bank form field'));
    }
  }
);

export const deleteBankFormField = createAsyncThunk(
  'masterdata/deleteBankFormField',
  async (fieldId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<IDeleteBankFormFieldResponse>('delete', `/api/organization/bank-form/delete/${fieldId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete bank form field'));
    }
  }
);

export const softDeleteBankFormField = createAsyncThunk(
  'masterdata/softDeleteBankFormField',
  async (fieldId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<ISoftDeleteBankFormFieldResponse>('delete', `/api/organization/bank-form/soft-delete/${fieldId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to soft delete bank form field'));
    }
  }
);



